import { createSelector } from 'reselect';

import { isUserPlanEnterprise, isUserPlanMemberBenefits, isUserPlanPro } from 'enums/user-plan';
import { UserType } from 'enums/user-type';
import { CompanyBusinessType } from 'enums/company-type';
import { CountryList } from 'enums/country';
import {
  makeSelectPreferences,
  makeSelectUserCompanyPrimaryUserId,
  makeSelectUserId,
  makeSelectUserPlan,
  makeSelectUserType,
  makeSelectUserCompany,
  makeSelectUserCountryCode,
} from 'reducers/user/selectors';

import { makeSelectUserRoles, makeSelectIsAuthenticated } from 'reducers/auth/selectors';

import { makeSelectFeatureFlagActive } from 'reducers/featureFlags/selectors';
import { UserFeatureFlags } from 'enums/user-membership-features';

const makeSelectOnboardingType = () =>
  createSelector(
    makeSelectUserId(),
    makeSelectUserType(),
    makeSelectUserPlan(),
    makeSelectUserRoles(),
    makeSelectIsAuthenticated(),
    makeSelectPreferences(),
    makeSelectUserCompanyPrimaryUserId(),
    makeSelectFeatureFlagActive(UserFeatureFlags.capitalReport),
    makeSelectUserCompany(),
    makeSelectUserCountryCode(),
    (
      userId,
      userType,
      userPlan,
      userRoles,
      isAuthenticated,
      userPreferences,
      primaryUserId,
      capitalReport,
      company,
      countryCode,
    ) => {
      const isLimitedOrLpp =
        CompanyBusinessType.limitedCompany === company.get('type') ||
        CompanyBusinessType.lppCompany === company.get('type') ||
        CompanyBusinessType.limitedLiabilityPartnership === company.get('type');

      if (
        userType === UserType.business &&
        isLimitedOrLpp &&
        countryCode === CountryList.GREAT_BRITAIN
      ) {
        const hasSeenBusinessFlow = userPreferences.getIn([
          'onboardingPreferences',
          'onboardingBusiness',
        ]);

        return !hasSeenBusinessFlow ? 'onboardingBusiness' : null;
      }

      if (
        userType === UserType.business &&
        !isLimitedOrLpp &&
        countryCode === CountryList.GREAT_BRITAIN
      ) {
        const hasSeenSoleTraderFlow = userPreferences.getIn([
          'onboardingPreferences',
          'onboardingBusinessSoleTrader',
        ]);

        return !hasSeenSoleTraderFlow ? 'onboardingBusinessSoleTrader' : null;
      }

      if (userType === UserType.business && countryCode === CountryList.SOUTH_AFRICA) {
        const hasSeenZAFlow = userPreferences.getIn([
          'onboardingPreferences',
          'onboardingBusinessZA',
        ]);

        return !hasSeenZAFlow ? 'onboardingBusinessZA' : null;
      }

      if (userType !== UserType.introducer || !userPlan || !userRoles || !isAuthenticated) {
        return null;
      }

      const hasSeenLightOwnerFlow = userPreferences.getIn([
        'onboardingPreferences',
        'onboardingFreeOwner',
      ]);
      const hasSeenLightUserFlow = userPreferences.getIn([
        'onboardingPreferences',
        'onboardingFreeUser',
      ]);
      const hasSeenProSponsorFlow = userPreferences.getIn([
        'onboardingPreferences',
        'onboardingProSponsor',
      ]);
      const hasSeenProUserFlow = userPreferences.getIn([
        'onboardingPreferences',
        'onboardingProUser',
      ]);
      const hasSeenProUserInvitedFlow = userPreferences.getIn([
        'onboardingPreferences',
        'onboardingProUserInvited',
      ]);
      const hasSeenMemberBenefitsFlow = userPreferences.get('onboardingMemberBenefits');
      const hasSeenOldOnboarding = userPreferences.get('onboardingIntroducerPartOne');
      const isPrimaryOwner = primaryUserId === userId;

      if (isUserPlanMemberBenefits(userPlan)) {
        return !hasSeenMemberBenefitsFlow ? 'onboardingMemberBenefits' : null;
      }

      if (isUserPlanPro(userPlan) || isUserPlanEnterprise(userPlan)) {
        if (hasSeenOldOnboarding || hasSeenLightUserFlow || hasSeenLightOwnerFlow) {
          if (isPrimaryOwner && !hasSeenProSponsorFlow) {
            return 'onboardingProSponsor';
          }

          return !hasSeenProUserFlow && !isPrimaryOwner ? 'onboardingProUser' : null;
        }

        return !hasSeenProUserInvitedFlow && !isPrimaryOwner ? 'onboardingProUserInvited' : null;
      }

      if (!hasSeenLightOwnerFlow && !hasSeenLightUserFlow) {
        return isPrimaryOwner ? 'onboardingFreeOwner' : 'onboardingFreeUser';
      }

      return null;
    },
  );

export { makeSelectOnboardingType };
